import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export interface Template {
  ID: number;
  OrgType: number;
  Title: string;
  Content: string;
  Link?: string;
}

export interface Announcement {
  TemplateID: any;
  ID: number;
  OrgID: number;
  Title: string;
  Content: string;
  Link?: string;
  StartDate: Date;
  EndDate: Date;
}

export interface AnnouncementData {
  active_announcements: Announcement[];
  expired_announcements: Announcement[];
}

export enum ContentLinkType {
	NONE = 'None',
    ANXIETY_SERIES = 'Anxiety Series',
    ANXIETY_SERIES_YOUTH = 'Anxiety Series for Youth',
    DEPRESSION_SERIES = 'Depression Series',
    // DEPRESSION_SERIES_YOUTH = 'Depression Series for Youth', // TODO: Add this back in when we have a youth depression series
    EMOTIONS_SERIES = 'Emotions Series',
    RESILIENCE_SKILLS_SERIES = 'Resilience Skills Series',
    INTERPERSONAL_VIOLENCE_SERIES = 'Interpersonal Violence Series',
    HEALTHY_RELATIONSHIPS_SERIES = 'Healthy Relationships Series',
    MINDFULNESS_SERIES = 'Mindfulness Series',
    PURPOSE_AND_MEANING_SERIES = 'Purpose and Meaning Series',
    SLEEP_SERIES = 'Sleep Series',
    RESILIENCE_AT_WORK_SERIES = 'Resilience at Work Series',
    MOODCHECK = 'Moodcheck',
    MENTAL_WELLBEING_ASSESSMENT = 'Mental Wellbeing Assessment',
    DASS_ASSESSMENT = 'DASS Assessment',
    GAD_7_ASSESSMENT = 'GAD-7 Assessment',
    PHQ_9_ASSESSMENT = 'PHQ-9 Assessment',
    BRCS_ASSESSMENT = 'BRCS Assessment',
    AUDIT_ASSESSMENT = 'AUDIT Assessment'
}

export const ContentLinkTypeList = Object.values(ContentLinkType);

export const OrgTypeList = [
    { id: 1, name: 'SBH', type: 'school'},
    { id: 2, name: 'EAP', type: 'EAP'},
    { id: 3, name: 'CMS', type: 'Community'},
    { id: 4, name: 'MC', type: 'Insurance Provider'}
];

export const ContentLinkTypeMap = {
    [ContentLinkType.NONE]: '',
    [ContentLinkType.ANXIETY_SERIES]: 'series-details/anxiety',
    [ContentLinkType.ANXIETY_SERIES_YOUTH]: 'series-details/anxiety_youth',
    [ContentLinkType.DEPRESSION_SERIES]: 'series-details/depression_new',
    // [ContentLinkType.DEPRESSION_SERIES_YOUTH]: 'series-details/depression_youth', // TODO: Add this back in when we have a youth depression series
    [ContentLinkType.EMOTIONS_SERIES]: 'series-details/emotions',
    [ContentLinkType.RESILIENCE_SKILLS_SERIES]: 'series-details/resilience',
    [ContentLinkType.INTERPERSONAL_VIOLENCE_SERIES]: 'series-details/empowerment',
    [ContentLinkType.HEALTHY_RELATIONSHIPS_SERIES]: 'series-details/healthyconnections',
    [ContentLinkType.MINDFULNESS_SERIES]: 'series-details/mindfulness',
    [ContentLinkType.PURPOSE_AND_MEANING_SERIES]: 'series-details/purpose',
    [ContentLinkType.SLEEP_SERIES]: 'series-details/sleep',
    [ContentLinkType.RESILIENCE_AT_WORK_SERIES]: 'series-details/work',
    [ContentLinkType.MOODCHECK]: 'mood-check',
    [ContentLinkType.MENTAL_WELLBEING_ASSESSMENT]: 'assessment/mentalwellbeing/listing',
    [ContentLinkType.DASS_ASSESSMENT]: 'assessment/dass/listing',
    [ContentLinkType.GAD_7_ASSESSMENT]: 'assessment/anxiety/listing',
    [ContentLinkType.PHQ_9_ASSESSMENT]: 'assessment/depression/listing',
    [ContentLinkType.BRCS_ASSESSMENT]: 'assessment/resilience/listing',  
    [ContentLinkType.AUDIT_ASSESSMENT]: 'assessment/alcoholuse/listing',
}

interface ApiResponse<T> {
  data: T;
  meta?: {
    organization_types?: string[];
    [key: string]: any;
  };
}

export function futureDateValidator(mode: 'create' | 'edit'): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const startDate = group.get('startDate')?.value;
    const endDate = group.get('endDate')?.value;
    const now = new Date();

    let errors: any = {};

    if (mode === 'create' && startDate && new Date(startDate) <= now) {
      errors.startDate = 'Start date must be in the future';
    }
    if (mode === 'create' && endDate && new Date(endDate) <= now) {
      errors.endDate = 'End date must be in the future';
    }
    if (mode === 'edit' && endDate && new Date(endDate) <= now) {
      errors.endDate = 'End date must be in the future';
    }

    return Object.keys(errors).length ? errors : null;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AnnouncementsService {
  private readonly baseUrl = 'api/v2/announcements';
  private readonly templatesUrl = 'api/v2/announcement-templates';

  constructor(private api: ApiService) { }

  // Template methods
  getTemplates(): Observable<Template[]> {
    return this.api.get(this.templatesUrl).pipe(
      map((response: ApiResponse<Template[]>) => response.data)
    );
  }

  getTemplateByOrgType(orgType: number): Observable<Template[]> {
    return this.api.get(`${this.templatesUrl}?orgType=${orgType}`).pipe(
      map((response: ApiResponse<Template[]>) => response.data)
    );
  }

  getTemplate(id: number): Observable<Template> {
    return this.api.get(`${this.templatesUrl}/${id}`).pipe(
      map((response: ApiResponse<Template>) => response.data)
    );
  }

  createTemplate(template: Template): Observable<Template> {
    return this.api.post(this.templatesUrl, template).pipe(
      map((response: ApiResponse<Template>) => response.data)
    );
  }

  updateTemplate(id: number, template: Template): Observable<Template> {
    return this.api.put(`${this.templatesUrl}/${id}`, template).pipe(
      map((response: ApiResponse<Template>) => response.data)
    );
  }

  deleteTemplate(id: number): Observable<void> {
    return this.api.delete(`${this.templatesUrl}/${id}`).pipe(
      map(() => void 0)
    );
  }

  // Announcement methods
  getAnnouncements(): Observable<AnnouncementData> {
    return this.api.get(this.baseUrl).pipe(
      map((response: ApiResponse<AnnouncementData>) => response.data)
    );
  }

  getAnnouncementsByOrgID(orgID: number): Observable<AnnouncementData> {
    return this.api.get(this.baseUrl+`?orgID=${orgID}`).pipe(
      map((response: ApiResponse<AnnouncementData>) => response.data)
    );
  }

  getAnnouncement(id: number): Observable<Announcement> {
    return this.api.get(`${this.baseUrl}/${id}`).pipe(
      map((response: ApiResponse<Announcement>) => response.data)
    );
  }

  createAnnouncement(announcement: Announcement): Observable<Announcement> {
    return this.api.post(this.baseUrl, announcement).pipe(
      map((response: ApiResponse<Announcement>) => response.data)
    );
  }

  updateAnnouncement(id: number, announcement: Announcement): Observable<Announcement> {
    return this.api.put(`${this.baseUrl}/${id}`, announcement).pipe(
      map((response: ApiResponse<Announcement>) => response.data)
    );
  }

  deleteAnnouncement(id: number): Observable<void> {
    return this.api.delete(`${this.baseUrl}/${id}`).pipe(
      map(() => void 0)
    );
  }

  convertOrgType(orgType: number): string {
    return OrgTypeList.find(o => o.id === orgType)?.name || '';
  }

} 